import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAdminAuth } from '../context/AdminAuthContext';

interface Props {
  title: string;
}

export default function AdminTopbar({ title }: Props) {
  const { theme, toggleTheme } = useTheme();
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const dark = theme === 'dark';
  const [dropOpen, setDropOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <header className={`h-16 flex items-center justify-between px-6 border-b ${dark ? 'bg-[#0D1B2A] border-white/10' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center gap-3">
        <img
          src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
          alt="Stingray"
          className={`md:hidden h-6 w-auto object-contain brightness-0 ${dark ? 'invert' : ''}`}
        />
        <h1 className={`text-lg font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
            dark
              ? 'bg-white/10 border-white/20 text-white hover:bg-white/20'
              : 'bg-gray-100 border-gray-200 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={dark ? 'ri-sun-line' : 'ri-moon-line'}></i>
          </div>
          {dark ? 'Light' : 'Dark'}
        </button>

        {/* View site */}
        <a
          href="/"
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
            dark
              ? 'bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10'
              : 'bg-gray-50 border-gray-200 text-gray-500 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-external-link-line"></i>
          </div>
          View Site
        </a>

        {/* User dropdown */}
        <div className="relative">
          <button
            onClick={() => setDropOpen((o) => !o)}
            className="flex items-center gap-2 cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-full text-white text-xs font-black" style={{ backgroundColor: 'var(--accent)' }}>
              {admin?.name?.charAt(0) ?? 'A'}
            </div>
            <div className="hidden sm:block text-left">
              <p className={`text-xs font-bold leading-tight ${dark ? 'text-white' : 'text-gray-900'}`}>{admin?.name ?? 'Admin'}</p>
              <p className={`text-[10px] leading-tight ${dark ? 'text-white/40' : 'text-gray-400'}`}>{admin?.role ?? 'Admin'}</p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`ri-arrow-down-s-line text-sm ${dark ? 'text-white/40' : 'text-gray-400'}`}></i>
            </div>
          </button>

          {dropOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setDropOpen(false)}></div>
              <div className={`absolute right-0 top-10 z-20 w-52 rounded-xl border py-1 ${dark ? 'bg-[#1C2B3A] border-white/10' : 'bg-white border-gray-200'}`}>
                <div className={`px-4 py-3 border-b ${dark ? 'border-white/10' : 'border-gray-100'}`}>
                  <p className={`text-xs font-bold ${dark ? 'text-white' : 'text-gray-900'}`}>{admin?.name}</p>
                  <p className={`text-xs mt-0.5 ${dark ? 'text-white/40' : 'text-gray-400'}`}>{admin?.email}</p>
                  <span
                    className="inline-block mt-1.5 text-[10px] font-bold px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: 'color-mix(in srgb, var(--accent) 20%, transparent)', color: 'var(--accent)' }}
                  >
                    {admin?.role}
                  </span>
                </div>
                <div className="py-1">
                  <a
                    href="/admin/settings"
                    className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${dark ? 'text-white/60 hover:text-white hover:bg-white/5' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-settings-3-line"></i>
                    </div>
                    Settings
                  </a>
                  <button
                    onClick={() => void handleLogout()}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-logout-box-line"></i>
                    </div>
                    Sign Out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
